import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { listBaskets, getCompetitorData, parseCsvFile, fetchCompetitorData } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/css/pricingBreakdown.css';

/* ──────────────────────────────────────────────── */
/* Rating Evolution — port of rating_evolution.py   */
/* ──────────────────────────────────────────────── */

function RatingEvolution() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [period, setPeriod] = useState('week');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [csvName, setCsvName] = useState(null);

    // ── Advanced Filters ──
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [brandsInitialized, setBrandsInitialized] = useState(false);

    const getInitialDateRange = () => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
        return { from: `${y}-${m}-01`, to: `${y}-${m}-${lastDay}` };
    };
    const [dateFrom, setDateFrom] = useState(getInitialDateRange().from);
    const [dateTo, setDateTo] = useState(getInitialDateRange().to);

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try { const res = await listBaskets(accountId); setBaskets(res.data || []); } catch { }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        setError('');
        try { const res = await getCompetitorData(selectedBasket.id, accountId); setData(res.data || []); }
        catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to load rating data.');
        } finally { setLoading(false); }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    // CSV upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        setError('');
        try {
            const res = await parseCsvFile(file);
            setData(res.data || []);
            setCsvName(file.name);
            setSelectedBasket({ id: 'csv', name: file.name });
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || 'Error parsing CSV.');
        }
        finally { setLoading(false); }
    };
    const handleClearCsv = () => { setCsvName(null); setSelectedBasket(null); setData([]); };

    const handleFetchData = async () => {
        if (!selectedBasket || selectedBasket.id === 'csv') return;
        if (!accountId) {
            setError('Select an Amazon Ads account before fetching data.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await fetchCompetitorData(selectedBasket.id, accountId);
            const refreshed = await getCompetitorData(selectedBasket.id, accountId);
            setData(refreshed.data || []);
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    const asins = useMemo(() => [...new Set(data.map(r => r.asin).filter(Boolean))], [data]);
    const allBrands = useMemo(() => [...new Set(data.map(r => r.brand).filter(Boolean))].sort(), [data]);
    const allYears = useMemo(() => [...new Set(data.map(r => r.date ? String(r.date).substring(0, 4) : null).filter(Boolean))].sort(), [data]);

    useEffect(() => {
        if (allBrands.length > 0 && !brandsInitialized) {
            setSelectedBrands(allBrands);
            setBrandsInitialized(true);
        }
    }, [allBrands, brandsInitialized]);

    // Reset when basket changes
    useEffect(() => {
        setBrandsInitialized(false);
        setSelectedBrands([]);
        const init = getInitialDateRange();
        setDateFrom(init.from);
        setDateTo(init.to);
        setSelectedDate('');
    }, [selectedBasket]);

    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const getYearWeek = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const startOfYear = new Date(year, 0, 1);
        const days = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
        const week = Math.ceil((days + startOfYear.getDay() + 1) / 7);
        return `${year}-W${String(week).padStart(2, '0')}`;
    };

    const getWeekLabel = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const startOfYear = new Date(year, 0, 1);
        const days = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
        const week = Math.ceil((days + startOfYear.getDay() + 1) / 7);
        return `${MONTH_NAMES[date.getMonth()]} ${year} Wk${week}`;
    };

    const getXKey = (r) => {
        if (period === 'day') return r.date;
        return getYearWeek(r.date) || '';
    };

    const getXValue = (r) => {
        if (period === 'day') return r.date;
        return getWeekLabel(r.date) || `Wk ${r.week}`;
    };

    const sortByDate = (arr) => arr.sort((a, b) => new Date(a.date) - new Date(b.date));

    const aggregateByWeek = (asinData) => {
        if (period !== 'week') return asinData;
        const weekMap = {};
        asinData.forEach(r => { weekMap[getXKey(r)] = r; });
        return sortByDate(Object.values(weekMap));
    };

    const aggregateData = (asinData) => {
        if (period === 'week') return aggregateByWeek(asinData);
        return asinData;
    };

    const filteredData = useMemo(() => {
        let fd = data;
        if (selectedBrands.length === 0) {
            return [];
        } else if (selectedBrands.length < allBrands.length) {
            fd = fd.filter(r => selectedBrands.includes(r.brand));
        }
        if (dateFrom) {
            fd = fd.filter(r => r.date >= dateFrom);
        }
        if (dateTo) {
            fd = fd.filter(r => r.date <= dateTo);
        }

        const map = {};
        fd.forEach(r => {
            if (!r.date || !r.asin) return;
            const key = `${r.asin}_${r.date}`;
            const existing = map[key];
            const isJS = String(r.data_source || '').toLowerCase().includes('jungle');
            if (!existing) {
                map[key] = r;
            } else {
                const existingIsJS = String(existing.data_source || '').toLowerCase().includes('jungle');
                if (isJS || (!isJS && !existingIsJS)) {
                    map[key] = r;
                }
            }
        });
        return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [data, selectedBrands, allBrands, dateFrom, dateTo]);

    const filteredAsins = useMemo(() => [...new Set(filteredData.map(r => r.asin).filter(Boolean))], [filteredData]);
    const groupKey = period === 'day' ? 'date' : 'week';

    const periodLabels = useMemo(() => {
        const labels = {};
        data.forEach(r => {
            const key = r[groupKey];
            if (key && !labels[key] && r.date) {
                if (period === 'week') {
                    const d = new Date(r.date);
                    const localMonth = d.toLocaleString('en-US', { month: 'short' });
                    labels[key] = `${localMonth} ${d.getUTCDate()}`;
                } else {
                    labels[key] = r.date;
                }
            }
        });
        return labels;
    }, [data, groupKey, period]);

    const buildOriginalTraces = () => {
        return filteredAsins.map(asin => {
            const rows = filteredData.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
            const brand = rows[0]?.brand || 'Unknown';
            return {
                x: rows.map(r => r.date),
                y: rows.map(r => r.product_star_rating != null ? Number(r.product_star_rating) : null),
                mode: 'lines+markers',
                name: `${brand} - ${asin}`,
                hovertemplate: `ASIN: ${asin}<br>Rating: %{y:.2f} ⭐<br>Reviews: %{customdata[0]}<br>Source: %{customdata[1]}<extra></extra>`,
                customdata: rows.map(r => [r.product_num_ratings || 0, r.data_source || 'RapidAPI']),
            };
        });
    };

    const buildTraces = () => {
        return filteredAsins.map(asin => {
            const rows = filteredData.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
            const brand = rows[0]?.brand || 'Unknown';
            const displayData = aggregateData(rows);
            return {
                x: displayData.map(r => r.date),
                y: displayData.map(r => r.product_star_rating != null ? Number(r.product_star_rating) : null),
                mode: 'lines+markers',
                name: `${brand} - ${asin}`,
                hovertemplate: `ASIN: ${asin}<br>Rating: %{y:.2f} ⭐<br>Date: %{customdata[1]}<br>Reviews: %{customdata[0]}<br>Source: %{customdata[2]}<extra></extra>`,
                customdata: displayData.map(r => [r.product_num_ratings || 0, getXValue(r), r.data_source || 'RapidAPI']),
            };
        });
    };

    const providerHint = useMemo(() => {
        if (!error) return '';
        const normalized = String(error).toLowerCase();
        if (normalized.includes('not subscribed') || normalized.includes('403')) {
            return 'Live provider access is restricted or not subscribed (Error 403). Please verify your RapidAPI subscription or use a historical tracking CSV.';
        }
        if (normalized.includes('too many requests') || normalized.includes('429')) {
            return 'Provider rate limit reached (Error 429). Retry in a few minutes or reduce ASIN volume.';
        }
        if (normalized.includes('external provider failed')) {
            return 'External provider failed for this request. Try again later or use a historical CSV.';
        }
        return '';
    }, [error]);

    const handleBrandToggle = (brand) => {
        setSelectedBrands(prev => prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]);
    };

    const [selectedDate, setSelectedDate] = useState('');
    const availableDates = useMemo(() => {
        return [...new Set(filteredData.map(r => r.date).filter(Boolean))].sort((a, b) => new Date(b) - new Date(a));
    }, [filteredData]);

    useEffect(() => {
        if (availableDates.length > 0 && (!selectedDate || !availableDates.includes(selectedDate))) {
            setSelectedDate(availableDates[0]);
        }
    }, [availableDates, selectedDate]);

    return (
        <Layout>
            <div className="container-fluid pb-container">
                <h2 className="pb-page-title">Rating Evolution</h2>
                <p className="pb-page-subtitle">
                    Track competitor reviews and star ratings over time.
                </p>

                {error && <div className="alert alert-danger">{error}</div>}
                {providerHint && <div className="alert alert-warning">{providerHint}</div>}

                <div className="row mb-3">
                    <div className="col-md-4">
                        <select className="form-select" value={selectedBasket?.id || ''}
                            onChange={e => setSelectedBasket(baskets.find(b => b.id === parseInt(e.target.value)) || null)}>
                            <option value="">Select basket...</option>
                            {baskets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                                <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                            </label>
                            {csvName && (
                                <span className="badge bg-success d-flex align-items-center gap-1">
                                    {csvName}
                                    <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="btn-group btn-group-sm w-100">
                            <button className={`btn ${period === 'week' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('week')}>Weekly</button>
                            <button className={`btn ${period === 'day' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod('day')}>Daily</button>
                        </div>
                    </div>
                    <div className="col-md-2">
                        <button
                            className="btn btn-success btn-sm w-100"
                            onClick={handleFetchData}
                            disabled={loading || !selectedBasket || selectedBasket.id === 'csv'}
                            title="Fetch latest competitor data"
                        >
                            Fetch Data
                        </button>
                    </div>
                </div>

                {loading && <div className="text-center my-4"><div className="spinner-border text-primary" /></div>}

                {!loading && selectedBasket && data.length === 0 && !error && (
                    <div className="alert alert-warning">
                        No hay datos para mostrar. Debes ejecutar Fetch Data en Tracking Overview. Si el proveedor falla (403/429), no se generan nuevos datos.
                    </div>
                )}

                {/* Brands filter card */}
                {!loading && data.length > 0 && (
                    <div className="card pb-filters-card mb-4">
                        <div className="pb-filters-header d-flex justify-content-between align-items-center">
                            <div>
                                <h6>Filters</h6>
                                <small>Refine the ratings data by selecting specific brands.</small>
                            </div>
                            <span className="badge bg-info text-dark">Filtered ASINs: {filteredAsins.length} / {asins.length}</span>
                        </div>
                        <div className="card-body">
                            {/* Date & Year Filters */}
                            <div className="row mb-3 pb-3 border-bottom">
                                <div className="col-12 d-flex align-items-center gap-2 flex-wrap">
                                    <span className="pb-filter-label mb-0 me-2">Time Period:</span>
                                    <button
                                        type="button"
                                        className={`btn btn-sm py-1 px-2 ${(!dateFrom && !dateTo) ? 'btn-primary fw-bold shadow-sm' : 'btn-outline-secondary'}`}
                                        onClick={() => { setDateFrom(''); setDateTo(''); }}
                                    >
                                        All Years ({allYears.length})
                                    </button>
                                    {allYears.map(year => {
                                        const isActive = dateFrom === `${year}-01-01` && dateTo === `${year}-12-31`;
                                        return (
                                            <button
                                                key={year}
                                                type="button"
                                                className={`btn btn-sm py-1 px-2 ${isActive ? 'btn-primary fw-bold shadow-sm' : 'btn-outline-secondary'}`}
                                                onClick={() => {
                                                    setDateFrom(`${year}-01-01`);
                                                    setDateTo(`${year}-12-31`);
                                                }}
                                            >
                                                {year}
                                            </button>
                                        );
                                    })}
                                    <div className="d-flex align-items-center gap-2 border-start ps-3 ms-2 flex-wrap">
                                        <span className="text-muted small fw-semibold">Date Range:</span>
                                        <input type="date" className="form-control form-control-sm" style={{ width: '130px', height: '30px' }}
                                            value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                        <span className="text-muted small">to</span>
                                        <input type="date" className="form-control form-control-sm" style={{ width: '130px', height: '30px' }}
                                            value={dateTo} onChange={e => setDateTo(e.target.value)} />
                                        {(dateFrom || dateTo) && (
                                            <button className="btn btn-sm btn-outline-danger py-0 px-2" style={{ fontSize: '0.75rem', height: '30px' }}
                                                onClick={() => { setDateFrom(''); setDateTo(''); }}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Brand Filters */}
                            <div className="row">
                                <div className="col-12">
                                    <span className="pb-filter-label">Brands to display</span>
                                    <div className="pb-brand-list">
                                        <div className="d-flex gap-2 mb-1">
                                            <button className="btn btn-outline-secondary btn-sm py-0" style={{ fontSize: '0.75rem' }} onClick={() => setSelectedBrands([...allBrands])}>All</button>
                                            <button className="btn btn-outline-secondary btn-sm py-0" style={{ fontSize: '0.75rem' }} onClick={() => setSelectedBrands([])}>None</button>
                                        </div>
                                        {allBrands.map(b => (
                                            <div key={b} className="form-check">
                                                <input className="form-check-input" type="checkbox" id={`flt-brand-${b}`}
                                                    checked={selectedBrands.includes(b)}
                                                    onChange={() => handleBrandToggle(b)} />
                                                <label className="form-check-label" htmlFor={`flt-brand-${b}`}>{b}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Chart 1: Star Rating Evolution */}
                {!loading && data.length > 0 && (
                    <div className="card pb-card mb-4">
                        <div className="pb-card-header">
                            <h5 className="mb-0">Star Rating Evolution</h5>
                        </div>
                        <div className="card-body">
                            <Plot
                                data={buildOriginalTraces()}
                                    layout={{
                                        height: Math.max(400, 100 * Math.ceil(asins.length / 3)),
                                        xaxis: { 
                                            title: { text: period === 'day' ? 'Date' : (period === 'month' ? 'Month' : 'Week'), standoff: 20 }, 
                                            type: 'date',
                                            tickformat: period === 'month' ? '%b %Y' : '%b %d, %Y',
                                            nticks: 10,
                                            tickangle: -45,
                                            tickfont: { size: 11 }
                                        },
                                        yaxis: { title: { text: 'Star Rating' }, range: [0, 5.5] },
                                        margin: { l: 60, r: 20, t: 50, b: 80 },
                                        hovermode: 'closest',
                                    }}
                                config={{ responsive: true }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}

                {/* Chart 2: Breakdown by ASIN — individual subplots grid */}
                {!loading && data.length > 0 && (
                    <div className="card pb-card mb-4">
                        <div className="pb-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 className="mb-0">Rating Evolution (by ASIN)</h5>
                        </div>
                        <div className="card-body">
                            {filteredAsins.length > 0 ? (
                                <div className="row">
                                    {filteredAsins.map(asin => {
                                        const asinData = filteredData.filter(r => r.asin === asin).sort((a, b) => new Date(a.date) - new Date(b.date));
                                        const brand = asinData[0]?.brand || 'Unknown';
                                        const displayData = aggregateData(asinData);
                                        return (
                                            <div className="col-md-4 mb-3" key={asin}>
                                                <Plot
                                                    data={[{
                                                        x: displayData.map(r => r.date),
                                                        y: displayData.map(r => r.product_star_rating != null ? Number(r.product_star_rating) : null),
                                                        mode: 'lines+markers',
                                                        line: { width: 2 },
                                                        marker: { size: 6 },
                                                        hovertemplate: `Rating: %{y:.2f} ⭐<br>Date: %{customdata[0]}<br>Source: %{customdata[1]}<extra></extra>`,
                                                        customdata: displayData.map(r => [getXValue(r), r.data_source || 'RapidAPI']),
                                                    }]}
                                                    layout={{
                                                        title: { text: `${brand} - ${asin}`, font: { size: 13, color: '#000' } },
                                                        height: 250,
                                                        xaxis: {
                                                            title: { text: period === 'day' ? 'Date' : (period === 'month' ? 'Month' : 'Week'), standoff: 15 },
                                                            showgrid: false,
                                                            type: 'date',
                                                            tickformat: period === 'month' ? '%b %Y' : '%b %d, %Y',
                                                            nticks: 6,
                                                            tickangle: -45,
                                                            tickfont: { size: 10 }
                                                        },
                                                        yaxis: { title: { text: 'Rating' }, range: [0, 5.5], showgrid: true },
                                                        margin: { l: 60, r: 15, t: 35, b: 65 },
                                                    }}
                                                    config={{ responsive: true, displayModeBar: false }}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="alert alert-info text-center py-4 my-2">
                                    No data matches the current date range or brand filters.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Pivot table (Rating History) */}
                {(() => {
                    const buildPivotTable = () => {
                        const keys = [...new Set(filteredData.map(r => getXKey(r)))].sort();
                        const periodCol = period === 'day' ? 'Date' : 'Week';
                        const rows = keys.map(key => {
                            const record = filteredData.find(r => getXKey(r) === key);
                            const label = record ? getXValue(record) : key;
                            const row = { [periodCol]: label };
                            filteredAsins.forEach(asin => {
                                const match = filteredData.find(r => r.asin === asin && getXKey(r) === key);
                                row[`${asin}`] = match?.product_star_rating != null ? Number(match.product_star_rating).toFixed(1) : '—';
                            });
                            return row;
                        });
                        return rows;
                    };
                    const pivotData = filteredData.length > 0 ? buildPivotTable() : [];
                    const pivotColumns = pivotData.length > 0 ? Object.keys(pivotData[0]) : [];

                    return pivotData.length > 0 && (
                        <div className="card mb-4">
                            <div className="card-header pb-card-header" id="headingHistory">
                                <h5 className="mb-0">
                                    <button className="btn btn-link text-decoration-none fw-bold" data-bs-toggle="collapse" data-bs-target="#collapseHistory">
                                        Rating History Table (Click to expand)
                                    </button>
                                </h5>
                            </div>
                            <div id="collapseHistory" className="collapse">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-striped table-hover text-center">
                                            <thead>
                                                <tr>{pivotColumns.map(c => <th key={c}>{c}</th>)}</tr>
                                            </thead>
                                            <tbody>
                                                {pivotData.map((row, i) => (
                                                    <tr key={i}>{pivotColumns.map(c => <td key={c}>{row[c]}</td>)}</tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Rating summary table */}
                {filteredData.length > 0 && (
                    <div className="card mt-4 pb-card">
                        <div className="pb-card-header">
                            <h5 className="mb-0">Current Ratings</h5>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-sm table-striped table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>ASIN</th><th>Brand</th><th>Rating</th><th>Reviews</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAsins.map(asin => {
                                            const latest = filteredData.filter(r => r.asin === asin).sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                                            const star = latest?.product_star_rating != null ? Number(latest.product_star_rating) : null;
                                            const ratings = latest?.product_num_ratings != null ? Number(latest.product_num_ratings) : null;
                                            return (
                                                <tr key={asin}>
                                                    <td className="font-monospace text-muted">{asin}</td>
                                                    <td className="fw-semibold text-dark">{latest?.brand || '—'}</td>
                                                    <td className="fw-bold text-success">⭐ {star != null ? star.toFixed(2) : '—'}</td>
                                                    <td>{ratings != null ? ratings.toLocaleString() : '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default RatingEvolution;
